"use client";

// ── the skill reading surface (/skills/[name]) ──────────────────────────────
// A skill is a document wearing a modifier, so this page is a reader wearing a
// manifest. Three things sit above the prose and nothing else does:
//
//   1. the title and what the skill makes you able to do,
//   2. THE PASTE LINE — the one sentence that explains the whole format: hand
//      this link to any AI and it knows what to do. It sits directly under the
//      headline because it is the instruction, not a footnote.
//   3. the diagnostic ledger — who kept it, when, which version, when to load
//      it, and — for a skill ingested from elsewhere on the web — where it came
//      from. Giving someone access to a skill should tell them more than giving
//      them the words would. The link we hand out stays OURS either way: the
//      raw route serves the document, the source is named, never hidden.
//
// Everything else is the house reading grammar: netti in full bloom beside the
// words (law_full_bloom: never frosted, aged or rested by the scroll), the
// reading scrim settling the ground behind it, and the one share/options dock
// bottom-left. The eyebrow and the title are the stage's navigator (law_stage):
// they fold into a title bar as the skill is read, and the document scrolls in
// its own area beneath.

import { useCallback, useRef, useState } from "react";
import BrandIcon from "../BrandIcon";
import OrganismBackdrop from "../OrganismBackdrop";
import Link from "next/link";
import SiteMenu from "../SiteMenu";
import ShareOptionsBar from "../os/artifact/ShareOptionsBar";
import SkillMarkdown from "./SkillMarkdown";
import { PillButton } from "../ui";
import { useNaturalHeight } from "../../lib/os/useNaturalHeight";
import Stage from "../stage/Stage";
import { useStageReadRamp } from "../stage/useStage";
import "../stage/surfaces/skills.css";
import { type OsNode } from "../../lib/os/tree";
import type { Skill, SkillVersion } from "../../lib/skills/types";
import VersionsRail from "./VersionsRail";
import { readerOrg, READER_ORG_WIDE, FULL_BLOOM_AGE } from "../../lib/os/readerOrganism";
import { useIsNarrow } from "../company/CompanyClient";
import { SKILL_PAGE_NIGHT, SKILL_PAGE_WASH } from "../community/communityTheme";
import { useRoomWash } from "../community/useRoomWash";
import "../community/night.css";
import { useCoTheme } from "../company/useCoTheme";
import { LinkBar } from "./PasteUrl";
import { useSignedIn } from "../../lib/supabase/useSignedIn";


/** "github.com/anthropics/skills" — the host and the repo, not the whole path. */
function sourceLabel(url: string): string {
  try {
    const u = new URL(url);
    const parts = u.pathname.split("/").filter(Boolean);
    return [u.host.replace(/^www\./, ""), ...parts.slice(0, 2)].join("/");
  } catch {
    return url;
  }
}

/** where a skill sits when it arrived as part of a package */
export interface SkillFolder {
  title: string;
  href: string;
  index: number;
  count: number;
  prev?: { title: string; href: string } | null;
  next?: { title: string; href: string } | null;
}

export default function SkillView({
  skill,
  url,
  versions = [],
  viewing = null,
  folder = null,
  saves = 0,
  sourceChecked = null,
}: {
  skill: Skill;
  url: string;
  /** every kept state of this skill, current first (empty = no history) */
  versions?: SkillVersion[];
  /** the superseded state being read, when `?v=` named one */
  viewing?: string | null;
  /** the package (folder of skills) this one sits in, when it sits in one —
   *  the crumb then says community / skills / <folder> / <skill> */
  folder?: SkillFolder | null;
  /** how many times it has been saved into someone's innernet (migration 109) */
  saves?: number;
  /** a brought skill: when its source was last read again (migration 123) */
  sourceChecked?: string | null;
}) {
  // asked in the browser, so the page itself can be cached (lib/supabase/useSignedIn).
  const signedIn = useSignedIn();
  // A SKILL PAGE IS A ROOM OF THE COMMONS. It used to open on the house cream,
  // so arriving from /community felt like leaving the place you were browsing.
  // It wears the skills room now — the same ground and the same creature the
  // skills space carries — and the ink ladder follows the hue with it.
  const theme = SKILL_PAGE_NIGHT;
  useCoTheme(theme.ground);
  useRoomWash(SKILL_PAGE_WASH);

  const basePath = `/skills/${skill.name}`;
  const pagePath = viewing ? `${basePath}?v=${encodeURIComponent(viewing)}` : basePath;
  const [menuOpen, setMenuOpen] = useState(false);
  const isNarrow = useIsNarrow();
  const org = readerOrg(isNarrow, READER_ORG_WIDE);
  const [copied, setCopied] = useState(false);

  // law_stage — the head is the stage's navigator: whose skill this is folds
  // away and the title settles into a bar (the stage folds it, with the scroll-up
  // reveal); the two halves publish their natural heights.
  const browRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  useNaturalHeight(browRef, "--lock-h", skill.name);
  useNaturalHeight(titleRef, "--lock-h", skill.name);

  // the reading ramp — settles the ground behind the prose and lights the seam.
  // One writer, from the stage's scroller (components/stage/useStage), never React
  // state on the scroll hot path. law_full_bloom (2026-09-26): netti here is always fully grown. It is NOT always sharp any more (law_focus_pull, 2026-10-19): a skill is read, so netti rests out of focus from the first frame (the stage's reading floor) and the ramp only settles the ground and the band.
  useStageReadRamp(240, "art");


  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* the link is on screen and selectable — nothing to recover */
    }
  }, [url]);

  // adapt the skill to an OsNode so the ONE share/options dock can act on it —
  // copy the link, download the markdown, native share.
  const node: OsNode = {
    key: skill.name,
    path: pagePath,
    kind: "artifact",
    name: skill.title,
    symbol: "",
    icon: "page",
    summary: skill.description,
    stats: [],
    document: skill.raw,
    createdAt: skill.updated ?? "",
    lastActiveAt: skill.updated ?? "",
  };

  const ledger: [string, React.ReactNode][] = [
    ["kind", "skill"],
    ...(skill.when_to_load ? ([["when to load it", skill.when_to_load]] as [string, React.ReactNode][]) : []),
    ...(skill.version ? ([["version", skill.version]] as [string, React.ReactNode][]) : []),
    ...(skill.updated ? ([["last tended", skill.updated]] as [string, React.ReactNode][]) : []),
    [
      // a skill somebody brought is its AUTHOR's — "kept by" is for a keeper who
      // tends it here; "written by" is for a name we are crediting.
      skill.brought_by ? "written by" : "kept by",
      skill.author_url ? (
        <a href={skill.author_url} className="quiet-link">{skill.authored_by ?? "innernet"}</a>
      ) : (
        skill.authored_by ?? "innernet"
      ),
    ],
    ...(skill.source_url
      ? ([[
          "source",
          <a key="src" href={skill.source_url} className="quiet-link" target="_blank" rel="noopener noreferrer">
            {sourceLabel(skill.source_url)}
          </a>,
        ]] as [string, React.ReactNode][])
      : []),
    // a brought skill follows its source: say when it was last read, so a
    // reader knows these are the author's current words, not an old copy.
    ...(sourceChecked
      ? ([["kept current", `with its source · last read ${sourceChecked.slice(0, 10)}`]] as [string, React.ReactNode][])
      : []),
    ...(saves > 0
      ? ([["saved", saves === 1 ? "once, into someone's innernet" : `${saves.toLocaleString("en")} times, into people's innernets`]] as [string, React.ReactNode][])
      : []),
    ...(folder
      ? ([[
          "in the folder",
          <span key="folder">
            <Link href={folder.href} className="quiet-link">{folder.title}</Link>
            {` · ${folder.index + 1} of ${folder.count}`}
            {folder.prev && <> · <Link href={folder.prev.href} className="quiet-link">‹ {folder.prev.title}</Link></>}
            {folder.next && <> · <Link href={folder.next.href} className="quiet-link">{folder.next.title} ›</Link></>}
          </span>,
        ]] as [string, React.ReactNode][])
      : []),
    ...(skill.brought_by ? ([["brought here by", skill.brought_by]] as [string, React.ReactNode][]) : []),
    ...(skill.license ? ([["reuse", skill.license]] as [string, React.ReactNode][]) : []),
    ...(skill.brought_by
      ? ([[
          "yours?",
          <span key="claim">
            it stays under your name. to change it or take it down, write to{" "}
            <a href={`mailto:connect@innernet.live?subject=${encodeURIComponent(`my skill on innernet: ${skill.name}`)}`} className="quiet-link">connect@innernet.live</a>
          </span>,
        ]] as [string, React.ReactNode][])
      : []),
    ["reading", `${skill.minutes} min`],
  ];

  return (
    <main className="sk-page cm-night" style={{ ["--bg" as string]: theme.ground } as React.CSSProperties}>
      <OrganismBackdrop
        // law_focus_pull (2026-10-19): a page that is read rests netti out of focus
        readingFrost
        fixed
        // the reader law, with its viewport made explicit: words left and
        // creature right only while there IS a right. On a phone it goes to the
        // bottom centre (lib/os/readerOrganism).
        anchor={org.anchor}
        offset={org.offset}
        size="min(130vh, 1100px)"
        blur={0}
        opacity={1}
        fade={0.94}
        color={theme.color}
        age={FULL_BLOOM_AGE}
        spin
        publishHue
        live={false}
        zIndex={org.zIndex}
      />

      <SiteMenu
        trail={
          folder
            ? [
                { label: "community", href: "/community" },
                { label: "skills", href: "/community" },
                { label: folder.title, href: folder.href, truncate: true },
                { label: skill.title, truncate: true },
              ]
            : [{ label: "community", href: "/community" }, { label: skill.title, truncate: true }]
        }
        signedIn={signedIn}
        current={{
          id: "skills",
          label: "skills",
          items: skill.outline.slice(0, 12).map((h) => ({
            key: h.id,
            label: h.text.toLowerCase(),
            onSelect: () => { window.location.hash = h.id; },
          })),
        }}
        hereKey="community"
        // THE one page with something to save: a stranger is looking at an
        // actual skill, so the honest verb is keeping it, not making an
        // account. Every other public surface says "create yours".
        ctaLabel="save it"
        ctaIntent="save-skill"
        onOpenChange={setMenuOpen}
      />

      {/* the reading ground — settles the ground behind the words, under netti */}
      <div className="sk-read-scrim" aria-hidden />

      {/* law_stage — the eyebrow and the title are the stage's navigator, over
          the top of the document's own scroll area. Its fold is a real collapse
          (fold="split"): whose skill this is folds away, the title settles into a
          bar, and the document rises with it. NO corner frosts: the seam at the
          navigator's edge lets the prose go, so it never scrolls over unfrosted
          chrome. */}
      <Stage
        fold="split"
        autoFold
        readFocus="always"
        top="clamp(5.5rem, 12vh, 7.5rem)"
        pin="clamp(3.6rem, 8vh, 4.6rem)"
        gap="0.7rem"
        slack="46vh"
        sceneKey={`${skill.name}:${viewing ?? ""}`}
        dir={1}
        label={skill.title}
        rootProps={{ className: "stage--skill" }}
        head={
          <div className="sk-col stage-skill__head">
            <header className="sk-head">
              <div ref={browRef} className="os-lock__fold">
                <p className="sk-eyebrow os-enter">
                  {skill.brought_by
                    ? <>a skill by {skill.authored_by ?? "its author"}, brought here by {skill.brought_by}</>
                    : <>a skill, kept by {skill.authored_by ?? "innernet"}</>}
                </p>
              </div>
              <div ref={titleRef} className="os-lock__title">
                <h1 className="sk-title os-enter" style={{ ["--i" as string]: 1 }}>{skill.title}</h1>
              </div>
            </header>
          </div>
        }
      >
        <article className="sk-col">
          {/* THE PASTE LINE — the whole format explained once, in place. */}
          <div className="sk-paste os-enter" style={{ ["--i" as string]: 2 }}>
            <p className="sk-paste-say">
              paste this link into your ai. it will know what to do.
            </p>
            <LinkBar url={url} done={copied} onCopy={copy} />
          </div>

          <p className="sk-dek os-enter" style={{ ["--i" as string]: 3 }}>{skill.description}</p>

          <div className="detail-rows sk-ledger os-enter" style={{ ["--i" as string]: 4 }}>
            {ledger.map(([label, value]) => (
              <div className="detail-row" key={label}>
                <span>{label}</span>
                <span>{value}</span>
              </div>
            ))}
          </div>

          {/* every state this skill has been — the one you're reading in ink */}
          <VersionsRail versions={versions} viewing={viewing} basePath={basePath} />

          <hr className="sk-rule sk-rule--head" />

          <SkillMarkdown body={skill.body} />

          {/* the gateway — no box, no banner: a closing line and THE netti
              pill, the front door's own grammar (law_gateway). One label, two
              doors — only where it lands differs. */}
          <section className="gateway" aria-labelledby="sk-gate-h">
            <hr className="sk-rule gateway-rule" aria-hidden />
            <h2 className="gateway-h" id="sk-gate-h">keep it where your ai can reach it.</h2>
            <p className="gateway-p">
              innernet is memory your ai tools read live — every skill, every project,
              every decision, in one place, connected once. save this skill to yours,
              or publish one of your own as a link like this.
            </p>
            <div className="gateway-row">
              <PillButton
                as="a"
                href={
                  signedIn
                    ? `/dashboard?add=skill:${skill.name}${viewing ? `&v=${encodeURIComponent(viewing)}` : ""}`
                    : `/signup?from=skill:${skill.name}${viewing ? `&v=${encodeURIComponent(viewing)}` : ""}`
                }
                icon={<BrandIcon name="innernet" size={17} className="pill-netti" />}
              >
                {viewing ? "save this version to your innernet" : "save it to your innernet"}
              </PillButton>
              <a className="quiet-link" href="/community">see every skill</a>
            </div>
          </section>
        </article>
      </Stage>

      {!menuOpen && (
        <ShareOptionsBar
          publicUrl={url}
          context={{ mode: "document", page: node }}
          projectId={null}
          sharePath={pagePath}
          variant="dock"
        />
      )}
    </main>
  );
}
